import React from 'react';
import { DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../../components/InputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import type { UspsProcessStatus } from '../graphql/uspsAddressProcessGraphql';

const { RangePicker } = DatePicker;

export interface FilterOptions {
  processStatus?: UspsProcessStatus;
  orderAddressId?: string;
  originalAddress?: string;
  createdRange?: [Dayjs | null, Dayjs | null];
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  processStatus: undefined,
  orderAddressId: undefined,
  originalAddress: undefined,
  createdRange: undefined,
};

interface FilterAreaProps {
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {
  const { t } = useTranslation();

  const handleTextChange = (key: 'orderAddressId' | 'originalAddress', value: string | undefined) => {
    const processed = typeof value === 'string' && value.trim() === '' ? undefined : value;
    setFilters({ ...filters, [key]: processed });
  };

  const columnLabels = [
    t('mapTool.uspsAddressProcess.filters.processStatus'),
    t('mapTool.uspsAddressProcess.filters.orderAddressId'),
    t('mapTool.uspsAddressProcess.filters.originalAddress'),
    t('mapTool.uspsAddressProcess.filters.createdRange'),
  ];
  const labelClassName = getSmartLabelClassName(columnLabels);

  const statusOptions: { value: UspsProcessStatus; label: string }[] = [
    { value: 'PENDING', label: t('enum.uspsProcessStatus.PENDING') as string },
    { value: 'SUCCESS', label: t('enum.uspsProcessStatus.SUCCESS') as string },
    { value: 'FAILED', label: t('enum.uspsProcessStatus.FAILED') as string },
  ];

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0 flex items-center gap-2">
          <span className={labelClassName}>
            {t('mapTool.uspsAddressProcess.filters.processStatus')}
          </span>
          <Select
            className="flex-1"
            allowClear
            placeholder={t('mapTool.uspsAddressProcess.placeholders.processStatus') as string}
            value={filters.processStatus}
            onChange={(v) => setFilters({ ...filters, processStatus: (v as UspsProcessStatus) || undefined })}
            options={statusOptions}
          />
        </div>
        <div className="min-w-0">
          <InputFilterItem
            label={t('mapTool.uspsAddressProcess.filters.orderAddressId')}
            value={filters.orderAddressId ?? ''}
            onChange={(e) => handleTextChange('orderAddressId', e.target.value)}
            spanClassName={labelClassName}
            placeholder={t('mapTool.uspsAddressProcess.placeholders.orderAddressIdExact') as string}
          />
        </div>
        <div className="min-w-0">
          <InputFilterItem
            label={t('mapTool.uspsAddressProcess.filters.originalAddress')}
            value={filters.originalAddress ?? ''}
            onChange={(e) => handleTextChange('originalAddress', e.target.value)}
            spanClassName={labelClassName}
            placeholder={t('mapTool.uspsAddressProcess.placeholders.originalAddressFuzzy') as string}
          />
        </div>
        <div className="min-w-0 flex items-center gap-2">
          <span className={labelClassName}>
            {t('mapTool.uspsAddressProcess.filters.createdRange')}
          </span>
          <RangePicker
            className="flex-1"
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={filters.createdRange}
            onChange={(dates) =>
              setFilters({
                ...filters,
                createdRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : undefined,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default FilterArea;
